import type { ClientAppointmentStatus } from '@cartwheel/api/v1'

// The function accepts `null` as a real status value (ghost appointments
// deleted from Healthie — see the spec description on ClientAppointmentStatus).
type NullableStatus = ClientAppointmentStatus | null

type AppointmentBadgeColor = 'mint' | 'orange' | 'gold' | 'brick' | undefined
type AppointmentDisplayStatus = 'Completed' | 'Missed' | 'Scheduled' | 'Cancelled' | 'Deleted'

// Statuses that represent a real, terminal outcome worth surfacing even when the
// appointment has been soft-deleted.
const TERMINAL_OUTCOME_STATUSES: NullableStatus[] = [
  'No-Show',
  'Late Cancellation',
  'Cancelled',
  'Occurred',
]

export function getAppointmentDisplayStatus(
  status: NullableStatus,
  softDeleted = false
): AppointmentDisplayStatus {
  // A soft-deleted appointment only reads "Deleted" when there is no real outcome to show —
  // e.g. a future/scheduled slot removed on clinician reassignment or when care ended. When it
  // has a terminal outcome (cancelled, late-cancel, no-show, occurred) surface that instead of
  // masking it with "Deleted". (RCM-1594)
  if (softDeleted && !TERMINAL_OUTCOME_STATUSES.includes(status)) {
    return 'Deleted'
  }

  switch (status) {
    case 'Occurred':
      return 'Completed'
    case 'No-Show':
    case 'Late Cancellation':
      return 'Missed'
    case 'Scheduled':
    case 'Checked-In':
      return 'Scheduled'
    case 'Re-Scheduled':
    case 'Cancelled':
      return 'Cancelled'
    case null:
      return 'Scheduled'
  }
}

export function getAppointmentStatusColor(
  status: NullableStatus,
  softDeleted = false
): AppointmentBadgeColor {
  switch (getAppointmentDisplayStatus(status, softDeleted)) {
    case 'Completed':
      return 'mint'
    case 'Missed':
      return 'orange'
    case 'Scheduled':
      return 'gold'
    case 'Deleted':
      return 'brick'
    default:
      return undefined
  }
}
